import { useGetAccount } from "apollo/hooks/queries";
import { TRACK_EVENTS } from "core/consts";
import Config from "core/model/config";
import { Account, ConfigType } from "core/types";
import { useEffect, useRef } from "react";
import { useTracking } from "react-tracking";
import { useLoggedInAccountId } from "reduxentities/hooks";
import { Locale, useLocale } from "translations";

const TRUSTED_SALESFORCE_DOMAINS = [
  /^https:\/\/[^/]+\.my\.site\.com/,
  /^https:\/\/[^/]+\.salesforce-scrt\.com/,
];

let globalScriptLoading = false;
let globalScriptLoaded = false;
let globalBootstrapInitialized = false;

function isValidSalesforceUrl(url: string): boolean {
  try {
    return TRUSTED_SALESFORCE_DOMAINS.some((pattern) => pattern.test(url));
  } catch {
    return false;
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    embeddedservice_bootstrap?: {
      settings: {
        language: Locale;
        chatButtonPosition?: string;
      };
      init: (
        orgId: string,
        embeddedServiceName: string,
        baseUrl: string,
        settings?: {
          scrt2URL?: string;
        },
      ) => void;
      prechatAPI?: {
        setVisiblePrechatFields: (fields: {
          [key: string]: {
            value: string;
            isEditableByEndUser: boolean;
          };
        }) => void;
      };
      utilAPI?: {
        launchChat?: () => Promise<void>;
        hideChatButton?: () => Promise<void>;
        showChatButton?: () => Promise<void>;
      };
    };
  }
}

function initEmbeddedMessaging(
  salesforceConfig: NonNullable<ConfigType["salesforce"]>,
) {
  try {
    if (!window.embeddedservice_bootstrap) {
      return;
    }

    // Prevent duplicate initialization
    if (globalBootstrapInitialized) {
      return;
    }

    const existingFrame = document.querySelector(
      '.embeddedMessagingFrame, iframe[id*="embeddedMessaging"]',
    );
    if (existingFrame) {
      globalBootstrapInitialized = true;
      return;
    }

    if (salesforceConfig.language) {
      window.embeddedservice_bootstrap.settings.language =
        salesforceConfig.language;
    }

    window.embeddedservice_bootstrap.init(
      salesforceConfig.orgId,
      salesforceConfig.embeddedServiceName,
      salesforceConfig.baseUrl,
      {
        scrt2URL: salesforceConfig.scrt2URL,
      },
    );

    globalBootstrapInitialized = true;
  } catch (err) {
    // Don't set flag on error to allow retry
    console.error("[Salesforce] Error initializing:", err);
  }
}

function setupPrechatFields(account: Account | null | undefined) {
  if (!account || !window.embeddedservice_bootstrap?.prechatAPI) {
    return;
  }

  const prechatFields: {
    [key: string]: {
      value: string;
      isEditableByEndUser: boolean;
    };
  } = {};

  if (account.first_name) {
    prechatFields._firstName = {
      isEditableByEndUser: false,
      value: account.first_name,
    };
  }

  if (account.last_name) {
    prechatFields._lastName = {
      isEditableByEndUser: false,
      value: account.last_name,
    };
  }

  if (account.email) {
    prechatFields._email = {
      isEditableByEndUser: false,
      value: account.email,
    };
  }

  if (Object.keys(prechatFields).length > 0) {
    window.embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields(
      prechatFields,
    );
  }
}

export default function SalesforceFrame() {
  const locale = useLocale();
  const salesforceConfig = {
    ...Config.salesforce,
    language: locale ?? Config.salesforce.language,
  };
  const accountId = useLoggedInAccountId();
  const { trackEvent } = useTracking();

  const [, account] = useGetAccount(accountId, {
    fetchPolicy: "cache-only",
  });

  const accountRef = useRef(account);

  useEffect(() => {
    accountRef.current = account;

    if (account && window.embeddedservice_bootstrap?.prechatAPI) {
      setupPrechatFields(account);
    }
  }, [account, window.embeddedservice_bootstrap?.prechatAPI]);

  useEffect(() => {
    if (!salesforceConfig) return;

    if (globalBootstrapInitialized) {
      return;
    }

    const attemptInit = () => {
      if (window.embeddedservice_bootstrap && !globalBootstrapInitialized) {
        initEmbeddedMessaging(salesforceConfig);
      }
    };

    if (window.embeddedservice_bootstrap) {
      attemptInit();
      return;
    }

    if (globalScriptLoading) {
      return;
    }

    if (globalScriptLoaded) {
      attemptInit();
      return;
    }

    if (!isValidSalesforceUrl(salesforceConfig.bootstrapScriptUrl)) {
      console.error(
        "[Salesforce] Invalid bootstrap script URL - not a trusted Salesforce domain:",
        salesforceConfig.bootstrapScriptUrl,
      );
      return;
    }

    globalScriptLoading = true;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = salesforceConfig.bootstrapScriptUrl;
    script.async = true;

    script.onload = () => {
      globalScriptLoading = false;
      globalScriptLoaded = true;
      attemptInit();
    };

    script.onerror = () => {
      globalScriptLoading = false;
      console.error(
        "[Salesforce] Failed to load bootstrap script:",
        salesforceConfig.bootstrapScriptUrl,
      );
    };

    document.body.appendChild(script);
  }, [salesforceConfig]);

  useEffect(() => {
    const injectPositioningCSS = () => {
      const styleId = "salesforce-chat-positioning";
      if (document.getElementById(styleId)) {
        return;
      }

      const style = document.createElement("style");
      style.id = styleId;
      // z-index 50: below app modals (z-modal is 102) so modals/popovers appear above the chat button
      style.textContent = `
        .embeddedMessagingFrame,
        iframe[id*="embeddedMessaging"],
        [class*="embeddedMessaging"],
        [class*="embeddedService"] {
          left: 20px !important;
          right: auto !important;
          z-index: 50 !important;
        }
      `;
      document.head.appendChild(style);
    };

    const setupSalesforceEventListeners = () => {
      // https://developer.salesforce.com/docs/service/messaging-web/guide/event-listeners.html

      const handleButtonClicked = () => {
        trackEvent({ name: TRACK_EVENTS.SALESFORCE_ICON_CLICKED });
      };

      const handleConversationOpened = () => {
        trackEvent({ name: TRACK_EVENTS.SALESFORCE_CHAT_OPENED });
        setTimeout(() => {
          setupPrechatFields(accountRef.current);
        }, 100);
      };

      const handleWindowClosed = () => {
        trackEvent({ name: TRACK_EVENTS.SALESFORCE_CHAT_CLOSED });
      };

      const handleConversationClosed = () => {
        // Conversation ended/terminated (different from window closed)
        trackEvent({ name: TRACK_EVENTS.SALESFORCE_CHAT_ENDED });
      };

      const handleWindowMinimized = () => {
        trackEvent({ name: TRACK_EVENTS.SALESFORCE_CHAT_MINIMIZED });
      };

      const handleWindowMaximized = () => {
        trackEvent({ name: TRACK_EVENTS.SALESFORCE_CHAT_MAXIMIZED });
      };

      const handleMessageSent = (event: Event | CustomEvent) => {
        try {
          const payload =
            event instanceof CustomEvent ? event.detail : undefined;

          if (!payload) {
            return;
          }

          // Salesforce payload structure varies - check multiple paths
          const senderRole =
            payload?.conversationEntry?.sender?.role ??
            payload?.sender?.role ??
            payload?.message?.source?.type;

          if (senderRole === "EndUser") {
            trackEvent({ name: TRACK_EVENTS.SALESFORCE_MESSAGE_SENT });
          } else if (
            senderRole === "Agent" ||
            senderRole === "Bot" ||
            senderRole === "Chatbot"
          ) {
            trackEvent({ name: TRACK_EVENTS.SALESFORCE_MESSAGE_RECEIVED });
          }
        } catch (error) {
          console.error(
            "[Salesforce] Error handling message sent event:",
            error,
          );
        }
      };

      const handleParticipantChanged = (event: Event | CustomEvent) => {
        try {
          const payload =
            event instanceof CustomEvent ? event.detail : undefined;
          if (payload?.participant?.state === "Active") {
            trackEvent({ name: TRACK_EVENTS.SALESFORCE_AGENT_CONNECTED });
          } else if (payload?.participant?.state === "Disconnected") {
            trackEvent({ name: TRACK_EVENTS.SALESFORCE_AGENT_DISCONNECTED });
          } else {
            // Fallback: assume connected if state is unknown
            trackEvent({ name: TRACK_EVENTS.SALESFORCE_AGENT_CONNECTED });
          }
        } catch (error) {
          console.error(
            "[Salesforce] Error handling participant changed event:",
            error,
          );
        }
      };

      window.addEventListener(
        "onEmbeddedMessagingButtonClicked",
        handleButtonClicked,
      );
      window.addEventListener(
        "onEmbeddedMessagingConversationOpened",
        handleConversationOpened,
      );
      window.addEventListener(
        "onEmbeddedMessagingWindowClosed",
        handleWindowClosed,
      );
      window.addEventListener(
        "onEmbeddedMessagingWindowMinimized",
        handleWindowMinimized,
      );
      window.addEventListener(
        "onEmbeddedMessagingWindowMaximized",
        handleWindowMaximized,
      );
      window.addEventListener("onEmbeddedMessageSent", handleMessageSent);
      // Conversation closed (ended/terminated) vs window closed are different events
      window.addEventListener(
        "onEmbeddedMessagingConversationClosed",
        handleConversationClosed,
      );
      // onEmbeddedMessagingConversationStarted duplicates onEmbeddedMessagingConversationOpened
      window.addEventListener(
        "onEmbeddedMessagingConversationParticipantChanged",
        handleParticipantChanged,
      );

      return () => {
        window.removeEventListener(
          "onEmbeddedMessagingButtonClicked",
          handleButtonClicked,
        );
        window.removeEventListener(
          "onEmbeddedMessagingConversationOpened",
          handleConversationOpened,
        );
        window.removeEventListener(
          "onEmbeddedMessagingWindowClosed",
          handleWindowClosed,
        );
        window.removeEventListener(
          "onEmbeddedMessagingWindowMinimized",
          handleWindowMinimized,
        );
        window.removeEventListener(
          "onEmbeddedMessagingWindowMaximized",
          handleWindowMaximized,
        );
        window.removeEventListener("onEmbeddedMessageSent", handleMessageSent);
        window.removeEventListener(
          "onEmbeddedMessagingConversationClosed",
          handleConversationClosed,
        );
        window.removeEventListener(
          "onEmbeddedMessagingConversationParticipantChanged",
          handleParticipantChanged,
        );
      };
    };

    let cleanupEventListeners: (() => void) | null = null;

    const applyPositioning = () => {
      injectPositioningCSS();
      const chatFrames = document.querySelectorAll(
        '.embeddedMessagingFrame, iframe[id*="embeddedMessaging"], [class*="embeddedMessaging"], [class*="embeddedService"]',
      );
      chatFrames.forEach((frame) => {
        const element = frame as HTMLElement;
        element.style.left = "20px";
        element.style.right = "auto";
      });

      cleanupEventListeners ??= setupSalesforceEventListeners();
    };

    injectPositioningCSS();

    const observer = new MutationObserver(() => {
      applyPositioning();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    applyPositioning();

    const handleReady = () => {
      setupPrechatFields(accountRef.current);
      applyPositioning();
    };

    window.addEventListener("onEmbeddedMessagingReady", handleReady);

    if (window.embeddedservice_bootstrap?.prechatAPI) {
      setupPrechatFields(accountRef.current);
    }

    return () => {
      window.removeEventListener("onEmbeddedMessagingReady", handleReady);
      observer.disconnect();
      if (cleanupEventListeners) {
        cleanupEventListeners();
        cleanupEventListeners = null;
      }
    };
  }, [trackEvent]);

  return null;
}
